/*!
 * boxGrid.js — where the live console's boxes go when nothing says
 * © 2026 Rob Canning — GPLv3
 *
 * A sound run from the console gets a box (its scope, its waveform). The
 * boxes nothing has placed -- no viz(uid, at:/size:), not dragged -- are
 * laid out together over the stage: a column down the middle while they
 * fit at full size, then more columns, filled a column at a time, and
 * smaller once even those do not fit. All of them always on the screen.
 * No DOM here (the node tests load it): audioFile.js places the boxes.
 */

/**
 * The grid for n boxes of w x h (and gap between them) in an area of
 * width x height, all in the same units: { scale, cells: [{ x, y, w, h }] },
 * each cell's top-left corner from the area's top-left, column by column.
 * The fewest columns that keep the boxes at full size; failing that, the
 * columns that keep them largest. Never larger than w x h.
 */
export function boxGrid(n, { width, height }, { w, h, gap = 0 }) {
  if (!(n > 0) || !(width > 0) || !(height > 0)) return { scale: 1, cells: [] };
  let best = null;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const scale = Math.min(1, width / (cols * w + (cols - 1) * gap), height / (rows * h + (rows - 1) * gap));
    if (!best || scale > best.scale + 1e-9) best = { cols, rows, scale };
    if (scale >= 1) break;
  }
  const { rows, scale } = best;
  const cols = Math.ceil(n / rows);                       // the columns used
  const bw = w * scale, bh = h * scale, g = gap * scale;
  const used = Math.min(n, rows);                         // the rows the first column fills
  const x0 = (width - (cols * bw + (cols - 1) * g)) / 2;
  const y0 = (height - (used * bh + (used - 1) * g)) / 2;
  const cells = [];
  for (let i = 0; i < n; i++) {
    const col = Math.floor(i / rows), row = i % rows;
    cells.push({ x: x0 + col * (bw + g), y: y0 + row * (bh + g), w: bw, h: bh });
  }
  return { scale, cells };
}
